import { create } from 'zustand';

import {
  Recognition,
  RECOGNITION_MODEL,
  RecognitionModel,
} from 'models/recognition.model';
import { AllStatuses, FileType } from 'interfaces';
import {
  DraftRecognitionKeys,
  DraftRecognitionValidationField,
  DraftSetOptions,
  RecognitionActions,
  RecognitionState,
} from './recognition.type';
import { LINK_LIMIT, REWARDS } from './recognition.constants';
import {
  validateAmountCompletedTask,
  validateDescription,
  validateTitle,
} from './recognition-validation-helper';

const initialRecognition: Recognition = {
  id: '',
  title: '',
  description: '',
  status: AllStatuses.NOT_SAVED,
  completedTasks: '',
  reward: '',
  stickerPackLink: '',
  feedbackFormLink: '',
  attachments: [],
  author: null,
  createdAt: '',
  updatedAt: '',
  type: 'BY_COUNT',
  taskIds: [],
};

const initialRecognitionErrors: DraftRecognitionKeys = {
  title: '',
  description: '',
  status: '',
  completedTasks: '',
  reward: '',
  stickerPackLink: '',
  feedbackFormLink: '',
  attachments: '',
  type: '',
  taskIds: '',
};

const initialState: RecognitionState = {
  recognitions: [],
  draft: initialRecognition,
  errors: initialRecognitionErrors,
};

const updateRecognitionList = (state: RecognitionState) => {
  const { recognitions, draft } = state;
  const index = recognitions.findIndex((rec) => rec.id === draft.id);

  if (index === -1) {
    return recognitions;
  }

  return recognitions.toSpliced(index, 1, draft);
};

const updateRecognitionField = (
  state: RecognitionState,
  options: DraftSetOptions & {
    field: keyof RecognitionState['draft'];
    value: RecognitionState['draft'][keyof RecognitionState['draft']];
    relatedErrorFields?: (keyof DraftRecognitionKeys)[];
  },
): RecognitionState => {
  const { draft, errors } = state;
  const { field, value, markAsDirty, relatedErrorFields = [] } = options;

  const updatedDraft: RecognitionState['draft'] = {
    ...draft,
    [field]: value,
    ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
  };

  const updatedRecognitions = updateRecognitionList({
    ...state,
    draft: updatedDraft,
  });

  const errorsToClear = [field, ...relatedErrorFields];

  return {
    errors: {
      ...errors,
      ...Object.fromEntries(
        errorsToClear.map((errorField) => [errorField, '']),
      ),
    },
    draft: updatedDraft,
    recognitions: updatedRecognitions,
  };
};

const updateLinks = (
  state: RecognitionState,
  markAsDirty: boolean,
  fieldName: Extract<
    keyof RecognitionState['draft'],
    'stickerPackLink' | 'feedbackFormLink'
  >,
  fieldValue: string,
): RecognitionState => {
  const { draft, errors } = state;

  const updatedDraft: RecognitionState['draft'] = {
    ...draft,
    [fieldName]: fieldValue,
    ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
  };

  const updatedRecognitions = updateRecognitionList({
    ...state,
    draft: updatedDraft,
  });

  if (fieldValue.length > LINK_LIMIT) {
    return {
      draft: updatedDraft,
      errors: {
        ...errors,
        [fieldName]: `Link must be less than ${LINK_LIMIT} characters long.
        You are ${fieldValue.length - LINK_LIMIT} characters over. Please use URL shortener`,
      },
      recognitions: updatedRecognitions,
    };
  }

  return updateRecognitionField(state, {
    field: fieldName,
    value: fieldValue,
    markAsDirty,
  });
};

const updateFiles = (
  state: RecognitionState,
  markAsDirty: boolean,
  files: FileType[],
): RecognitionState => {
  const { draft } = state;

  const updatedDraft: RecognitionState['draft'] = {
    ...draft,
    attachments: files,
    ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
  };

  const updatedRecognitions = updateRecognitionList({
    ...state,
    draft: updatedDraft,
  });

  return {
    ...state,
    draft: updatedDraft,
    recognitions: updatedRecognitions,
  };
};

export const useRecognitionStore = create<
  RecognitionState & RecognitionActions
>((set, get) => ({
  ...initialState,
  actions: {
    setDraftTitle: (
      title: string,
      { markAsDirty, shouldValidate }: DraftSetOptions = {
        markAsDirty: true,
        shouldValidate: true,
      },
    ) =>
      set((state) => {
        const { draft, errors } = state;

        const updatedDraft: RecognitionState['draft'] = {
          ...draft,
          title: title ?? 'Title',
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedRecognitions = updateRecognitionList({
          ...state,
          draft: updatedDraft,
        });

        const titleError = shouldValidate && validateTitle(title, true);

        if (titleError) {
          return {
            ...state,
            draft: updatedDraft,
            errors: {
              ...errors,
              title: titleError,
            },
            recognitions: updatedRecognitions,
          };
        }

        return updateRecognitionField(state, {
          field: 'title',
          value: title,
          markAsDirty,
        });
      }),
    setDraftDescription: (
      description: string,
      { markAsDirty, shouldValidate }: DraftSetOptions = {
        markAsDirty: true,
        shouldValidate: true,
      },
    ) =>
      set((state) => {
        const { draft, errors } = state;

        const updatedDraft: RecognitionState['draft'] = {
          ...draft,
          description,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedRecognitions = updateRecognitionList({
          ...state,
          draft: updatedDraft,
        });

        const descriptionError =
          shouldValidate && validateDescription(description);

        if (descriptionError) {
          return {
            ...state,
            draft: updatedDraft,
            errors: {
              ...errors,
              description: descriptionError,
            },
            recognitions: updatedRecognitions,
          };
        }

        return updateRecognitionField(state, {
          field: 'description',
          value: description,
          markAsDirty,
        });
      }),
    setDraftType: (
      type: RecognitionModel,
      { markAsDirty }: DraftSetOptions = {
        markAsDirty: true,
      },
    ) =>
      set((state) => {
        const { draft, errors } = state;

        const updatedDraft: RecognitionState['draft'] = {
          ...draft,
          type,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedRecognitions = updateRecognitionList({
          ...state,
          draft: updatedDraft,
        });

        return updateRecognitionField(state, {
          field: 'type',
          value: type,
          markAsDirty,
        });
      }),
    setDraftTaskIds: (
      ids: string[] = [],
      { markAsDirty }: DraftSetOptions = {
        markAsDirty: true,
      },
    ) =>
      set((state) => {
        const { draft, errors } = state;

        const updatedDraft: RecognitionState['draft'] = {
          ...draft,
          taskIds: [...ids],
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedRecognitions = updateRecognitionList({
          ...state,
          draft: updatedDraft,
        });

        const error = validateAmountCompletedTask(1, false, draft.type, ids);

        if (error) {
          return {
            ...state,
            draft: updatedDraft,
            errors: {
              ...errors,
              completedTasks: error,
            },
            recognitions: updatedRecognitions,
          };
        }

        return updateRecognitionField(state, {
          field: 'taskIds',
          value: [...ids],
          markAsDirty,
          relatedErrorFields: ['completedTasks'],
        });
      }),
    setDraftAmountCompletedTasks: (
      completedTasks: string,
      { markAsDirty, shouldValidate }: DraftSetOptions = {
        markAsDirty: true,
        shouldValidate: true,
      },
    ) =>
      set((state) => {
        const { draft, errors } = state;

        const updatedDraft: RecognitionState['draft'] = {
          ...draft,
          completedTasks,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedRecognitions = updateRecognitionList({
          ...state,
          draft: updatedDraft,
        });

        const amountError =
          shouldValidate &&
          validateAmountCompletedTask(+completedTasks, false, draft.type);

        if (amountError) {
          return {
            ...state,
            draft: updatedDraft,
            errors: {
              ...errors,
              completedTasks: amountError,
            },
            recognitions: updatedRecognitions,
          };
        }

        return updateRecognitionField(state, {
          field: 'completedTasks',
          value: completedTasks,
          markAsDirty,
          relatedErrorFields: ['taskIds'],
        });
      }),
    setDraftReward: (
      rewardId: number,
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) =>
      set((state) =>
        updateRecognitionField(state, {
          field: 'reward',
          value: REWARDS.find((r) => r.id === rewardId)?.value ?? '',
          markAsDirty,
        }),
      ),
    setDraftStickerpack: (
      stickerPackLink: string,
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) =>
      set((state) =>
        updateLinks(state, markAsDirty, 'stickerPackLink', stickerPackLink),
      ),
    setDraftFeedback: (
      feedbackFormLink: string,
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) =>
      set((state) =>
        updateLinks(state, markAsDirty, 'feedbackFormLink', feedbackFormLink),
      ),
    setDraftPictures: (
      files: FileType[],
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) => set((state) => updateFiles(state, markAsDirty, files)),
  },
}));

export const setDraftRecognition = (recognition: Recognition) => {
  useRecognitionStore.setState({
    draft: {
      ...initialState.draft,
      ...recognition,
    },
    errors: initialState.errors,
  });
};

export const setRecognitionErrors = (
  newErrors: DraftRecognitionValidationField,
) => {
  const { errors } = useRecognitionStore.getState();

  useRecognitionStore.setState({
    errors: {
      ...errors,
      ...newErrors,
    },
  });
};

export const resetDraft = () => {
  useRecognitionStore.setState({
    draft: initialState.draft,
    errors: initialState.errors,
  });
};

export const setRecognitions = (recognitions: Recognition[]) => {
  useRecognitionStore.setState({ recognitions });
};

export const deleteDraft = (id: string) => {
  useRecognitionStore.setState((state) => ({
    recognitions: state.recognitions.filter(
      (recognition) => recognition.id !== id,
    ),
  }));
};

export const recognitionTemplate = initialRecognition;
